import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import type { Token } from '../../@types/Token';
import { signIn, signOut } from '../../actions/auth';
import type { RootState } from '../../reducers';
import { Main, MainWrapper, Nav } from '../../styled-components/components/Main';
import MainNavigation, {
  type Page,
  type PagePartial,
} from '../../styled-components/components/MainNavigation';
import { PortalElement, PortalNamespace } from '../../styled-components/components/Portal';
import Snackbar from '../../styled-components/components/Snackbar';
import { SpinnerOverlay } from '../spinner';
import DealerAdminBanner from './DealerAdminBanner';
import GlobalProviders from './GlobalProviders';

interface AppProps {
  children: React.ReactNode;
}

let hasSignedIn = false;
const App = ({ children }: AppProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const token = useSelector<RootState, string | null | undefined>((s) => s.auth.token);
  const decodedToken = useSelector<RootState, Token | undefined>((s) => s.auth.decodedToken);

  const actions = bindActionCreators(
    {
      signOut,
      signIn,
    },
    dispatch,
  );

  useEffect(() => {
    // To overcome second render when using <StrictMode>, multiple signins at same time makes app fail
    if (!hasSignedIn) {
      hasSignedIn = true;
      actions.signIn(navigate);
    }
  }, []);

  if (!token) {
    return <SpinnerOverlay />;
  }

  if (decodedToken && decodedToken.role !== 'wayke-admin') {
    return (
      <div className="container text-center m-y-double">
        <div data-am-content="" className="m-b">
          <h1>Forbidden</h1>
          <p>You don&apos;t have permission to view this service.</p>
        </div>
        <div>
          <button type="button" data-am-button="primary" onClick={actions.signOut}>
            Sign out or sign in with a different account
          </button>
        </div>
      </div>
    );
  }

  const navigationTree: PagePartial[] = [
    {
      title: 'Dashboard',
      description: 'Dashboard',
      url: '/',
      isActiveFilter: 'exact',
      availableOnMarket: ['NO', 'SE', 'US'],
    },
    {
      title: 'Unarchive',
      description: 'Unarchive',
      url: '/unarchive',
      availableOnMarket: ['NO', 'SE', 'US'],
    },
    {
      url: '/settings',
      description: 'Settings',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Settings',
    },
    {
      url: '/permissions',
      description: 'Memberships',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Memberships',
    },
    {
      url: '/administrators',
      description: 'Administrators',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Administrators',
    },
    {
      url: '/branches',
      description: 'Branches',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Branches',
    },
    {
      url: '/financing-providers',
      description: 'Financing providers',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Financing providers',
    },
    {
      url: '/synonyms',
      description: 'Synonyms',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Synonyms',
    },
    {
      url: '/users',
      description: 'Users',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Users',
    },
    {
      url: '/wxr-boost',
      description: 'WXR Boost',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'WXR Boost',
    },
    {
      url: '/system-clients',
      description: 'System clients',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'System clients',
    },
    {
      url: '/vehicle-view',
      description: 'Vehicle view',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Vehicle view',
    },
    {
      url: '/blocket-view',
      description: 'Blocket view',
      availableOnMarket: ['SE'],
      title: 'Blocket view',
    },
    {
      url: '/blocket-outbox-stats',
      description: 'Blocket outbox stats',
      availableOnMarket: ['SE'],
      title: 'Blocket outbox stats',
    },
    {
      url: '/cs-reports',
      description: 'CS Reports',
      availableOnMarket: ['SE'],
      title: 'CS Reports',
    },
    {
      url: '/virtual-vehicle',
      description: 'Virtual vehicles',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Virtual vehicles',
    },
    {
      url: '/bankid',
      description: 'BankID',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'BankID',
    },
    {
      url: '/websites',
      description: 'Websites',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Websites',
    },
    {
      url: '/accessories',
      description: 'Accessories',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Accessories',
    },
    {
      url: '/assignment-options',
      description: 'Assignment options',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Assignment options',
    },
    {
      url: '/seo',
      description: 'SEO texts',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'SEO texts',
    },
    {
      url: '/profile',
      description: 'Profile',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Profile',
    },
    {
      url: '/mass-deletion-protection',
      description: 'Mass deletion protection',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Mass deletion protection',
    },
    {
      url: '/alert-banners',
      description: 'Alert banners',
      availableOnMarket: ['NO', 'SE', 'US'],
      title: 'Alert banners',
    },
  ];

  const isActive = (p?: string) => path.startsWith(p || '');
  const isActiveExactMatch = (p?: string) => path === (p || '');

  const resolvedNavigationTree = navigationTree.map((page, i): Page => {
    const enrichedPages: Page[] | undefined = page.pages?.map(
      (p, i): Omit<Page, 'pages'> => ({
        ...p,
        key: `${page.url}-${i}`,
        isActive: p.isActiveFilter === 'exact' ? isActiveExactMatch(p.url) : isActive(p.url),
      }),
    );

    return {
      ...page,
      pages: enrichedPages,
      key: `${page.url}-${i}`,
      isActive: page.isActiveFilter === 'exact' ? isActiveExactMatch(page.url) : isActive(page.url),
    };
  });

  return (
    <GlobalProviders token={token}>
      <Snackbar />
      <MainWrapper>
        <Nav>
          <MainNavigation onSignOut={actions.signOut} navigationTree={resolvedNavigationTree} />
        </Nav>
        <Main>
          <DealerAdminBanner />
          {children}
        </Main>
      </MainWrapper>
      <PortalElement id={PortalNamespace.DefaultPortal} />
    </GlobalProviders>
  );
};

export default App;
